/**
 * Google Consent Mode v2 for app.humanriskacademy.com.
 *
 * The app has exactly one tracking purpose — analytics (GA4, via GTM). It runs
 * no ad or CRM tags, so the banner is a single grant/deny rather than a category
 * matrix, and `ad_*` stays `denied` unconditionally.
 *
 * This is a SECOND, independent consent record from Klaro's on w-z.com: consent
 * is per-origin and the two are different registrable domains, so a visitor who
 * accepted there is asked again here. What must match across the properties is
 * the SIGNAL (`gtag('consent', …)`), not the library or the stored decision.
 */

import { CONSENT_OPT_IN_REGIONS } from './consent-regions';
import { gtag } from './gtag';

const CONSENT_VERSION = 1;

export const CONSENT_STORAGE_KEY = 'wz_consent';

interface StoredConsent {
  v: number;
  analytics: boolean;
}

/**
 * Declare defaults before GTM (or gtag.js) can evaluate a single tag.
 *
 * TWO defaults, scoped by region: opt-in inside the EEA/UK/CH, analytics on by
 * default everywhere else. Google resolves `region` from the request IP and the
 * more specific entry wins, so ordering is documentation, not logic. `ad_*`
 * stays denied worldwide — the app runs no ad tags at all.
 *
 * These must match w-z.com's defaults exactly. The cross-domain linker stitches
 * both domains into ONE GA4 session; if the two halves disagreed on the default,
 * a visitor's consent state would flip mid-session at the hand-off.
 *
 * `wait_for_update: 500` is load-bearing. Without it GTM evaluates tags the
 * instant it loads, sees the default, and fires before React has mounted and
 * read localStorage — so a returning visitor's stored decision would be ignored
 * on every first paint. The 500ms window is where `replayStoredConsent()` lands.
 */
export function pushConsentDefaults(): void {
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    region: CONSENT_OPT_IN_REGIONS,
    wait_for_update: 500,
  });
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
    wait_for_update: 500,
  });
}

/** Record the visitor's choice and tell Google about it. */
export function applyConsent(analyticsGranted: boolean): void {
  gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
  });

  try {
    const payload: StoredConsent = { v: CONSENT_VERSION, analytics: analyticsGranted };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage disabled — the signal still applies for this page view */
  }
}

/** `null` means "hasn't chosen yet", which is what shows the banner. */
export function readStoredConsent(): boolean | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.v !== CONSENT_VERSION || typeof parsed.analytics !== 'boolean') return null;
    return parsed.analytics;
  } catch {
    return null;
  }
}

/** Re-assert a previous decision inside the `wait_for_update` window. */
export function replayStoredConsent(): void {
  const stored = readStoredConsent();
  if (stored === null) return;
  applyConsent(stored);
}

/** Clear the decision so the banner reappears ("Cookie settings"). */
export function clearStoredConsent(): void {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
